import forEach from "lodash/forEach";
import {
  FORM_ROLES_KEY,
  FORM_SUBTEMPLATES_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useMultiActivityFormStoreActions } from "PFApp/stores/activity_form/multi_roles_form";
import { useManageRolesStore, useManageRolesStoreActions } from "PFApp/stores/manage_roles";
import { ManageRolesMultiForm } from "PFApp/types/activity_form/manage_roles_form_schema";
import { useInvalidateManageViewRoles } from "PFCore/hooks/queries/roles/use_invalidate_manage_view_roles";
import { ManageRolesViewRoleItem } from "PFCore/types/manage_roles_view";
import { Profile } from "PFCore/types/profile";
import { MutableRefObject, PropsWithChildren, useCallback, useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useUnmount } from "react-use";

import { useManageRolesInitialRoleData } from "../hooks/use_manage_roles_initial_role_data";
import { usePendingEditsSync } from "../hooks/use_pending_edits_sync";
import { groupRolesIntoForm } from "../manage_roles.utils";
import { PendingEditsSyncContext } from "./pending_edits_sync_context";

type ManageRolesFormProviderProps = PropsWithChildren<{
  entries: ManageRolesViewRoleItem[];
}>;

type ManageRolesFormProviderInnerProps = PropsWithChildren<{
  currentPage: number;
  trackerResetVersionRef: MutableRefObject<number>;
}>;

const ManageRolesFormProviderInner = ({
  children,
  currentPage,
  trackerResetVersionRef
}: ManageRolesFormProviderInnerProps) => {
  const { invalidatePage, removeAll } = useInvalidateManageViewRoles();

  const { hasPendingEdits } = useManageRolesStoreActions();
  const { flushPendingEdits, pauseSync, resumeSync } = usePendingEditsSync();

  const prevPageRef = useRef(currentPage);

  useEffect(() => {
    if (prevPageRef.current === currentPage) {
      return;
    }
    const previousPage = prevPageRef.current;
    prevPageRef.current = currentPage;
    invalidatePage(previousPage);
  }, [currentPage, invalidatePage]);

  // Remove (not just invalidate) the query cache on unmount so that re-entering the page
  // starts with a fresh fetch. Invalidation alone leaves stale data in the cache, which
  // react-query serves immediately on remount while refetching in the background. That
  // two-step form population (stale → fresh) causes the pending edits tracker to
  // misinterpret the data refresh as a user edit, firing a duplicate update request.
  useUnmount(removeAll);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasPendingEdits()) {
        // Shows native "Leave page?" dialog. If the user confirms, unsaved edits will be lost.
        // Async flushing is not viable here — the browser does not await promises in beforeunload,
        // and navigator.sendBeacon only supports POST without custom auth headers.
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasPendingEdits]);

  return (
    <PendingEditsSyncContext.Provider
      value={{ flushPendingEdits, pauseSync, resumeSync, trackerResetVersionRef }}
    >
      {children}
    </PendingEditsSyncContext.Provider>
  );
};

export const ManageRolesFormProvider = ({ children, entries }: ManageRolesFormProviderProps) => {
  const { setRoleData, clearRoleData } = useMultiActivityFormStoreActions();
  const { getInitialRoleData } = useManageRolesInitialRoleData();

  const currentPage = useManageRolesStore((state) => state.currentPage);

  const methods = useForm<ManageRolesMultiForm>({
    reValidateMode: "onSubmit"
  });

  const profileByUniqueIdRef = useRef<Map<string, Profile | undefined>>(new Map());
  // Bumped on every programmatic form reset so the pending-edits trackers re-baseline
  // instead of misreading the server-driven repopulation as user edits.
  const trackerResetVersionRef = useRef(0);

  const { formData, profileByUniqueId } = useMemo(() => {
    const formEntries = entries.map(getInitialRoleData);
    const profileMap = new Map(formEntries.map((formRole, i) => [formRole.uniqueId, entries[i].profile]));
    return { formData: groupRolesIntoForm(formEntries), profileByUniqueId: profileMap };
  }, [entries]);

  profileByUniqueIdRef.current = profileByUniqueId;

  const syncFormPage = useCallback(
    (nextFormData: ManageRolesMultiForm) => {
      const previousValues = methods.getValues();

      forEach(previousValues[FORM_SUBTEMPLATES_KEY], (subtemplate) => {
        forEach(subtemplate[FORM_ROLES_KEY], (role) => {
          clearRoleData(role.uniqueId);
        });
      });

      trackerResetVersionRef.current += 1;
      methods.reset(nextFormData);

      forEach(nextFormData[FORM_SUBTEMPLATES_KEY], (subtemplate) => {
        forEach(subtemplate[FORM_ROLES_KEY], (role) => {
          setRoleData(role.uniqueId, {
            activityCreator: profileByUniqueIdRef.current.get(role.uniqueId) ?? null,
            attachments: []
          });
        });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [methods]
  );

  useEffect(() => {
    syncFormPage(formData);
  }, [formData, syncFormPage]);

  return (
    <FormProvider {...methods}>
      <ManageRolesFormProviderInner currentPage={currentPage} trackerResetVersionRef={trackerResetVersionRef}>
        {children}
      </ManageRolesFormProviderInner>
    </FormProvider>
  );
};
